"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useRef, useState, useTransition } from "react";
import { getGetCurrentUserQueryKey, updateCurrentUserLocale } from "@/app/generated/api/auth/auth";
import type { CurrentUserResponse, UserDto } from "@/app/generated/api/schemas";
import {
  LanguageDropdown,
  LanguageDropdownOptions,
  LanguageFlag,
} from "@/app/components/LanguageDropdown";
import { AdminLocale, locales, resolveAdminLocale } from "@/i18n/locale";
import { setAdminLocaleCookie } from "@/i18n/client";

type LanguageSwitcherProps = {
  user: UserDto;
  disabled?: boolean;
  onLocaleChanged?: () => Promise<void> | void;
};

export function LanguageSwitcher({ user, disabled = false, onLocaleChanged }: LanguageSwitcherProps) {
  const { t, activeLocale, saving, switchLocale } = useAdminLocaleSwitcher(user, onLocaleChanged);

  return (
    <LanguageDropdown
      activeCode={activeLocale}
      ariaLabel={t("switch")}
      currentLabel={t("current")}
      disabled={disabled || saving !== null}
      label={t("label")}
      onSelect={switchLocale}
      options={locales.map((locale) => ({
        code: locale,
        name: t(locale),
      }))}
      variant="toolbar"
    />
  );
}

export function GuestLanguageSwitcher({ compact = false }: { compact?: boolean } = {}) {
  const currentRequestLocale = useLocale();
  const t = useTranslations("common.adminLanguage");
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const activeLocale = resolveAdminLocale(currentRequestLocale);

  function switchLocale(locale: AdminLocale) {
    if (locale === activeLocale || isPending) {
      return;
    }

    setAdminLocaleCookie(locale);
    startTransition(() => {
      router.refresh();
    });
  }

  return (
    <div className={compact ? "mt-3 flex justify-center" : "mt-6 flex flex-col items-center gap-2 border-t border-border pt-5"}>
      <span className={compact ? "sr-only" : "text-xs font-semibold text-text-muted"}>{t("label")}</span>
      <div
        className="grid max-w-full grid-cols-2 rounded-xl border border-border bg-bg-elevated p-1 shadow-sm sm:grid-cols-3"
        role="radiogroup"
        aria-label={t("switch")}
      >
        {locales.map((locale) => {
          const active = locale === activeLocale;

          return (
            <button
              key={locale}
              type="button"
              role="radio"
              aria-checked={active}
              className={`inline-flex min-w-24 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                active
                  ? "bg-primary/15 text-primary"
                  : "text-text-secondary hover:bg-surface-hover hover:text-text"
              }`}
              onClick={() => switchLocale(locale)}
              disabled={isPending}
            >
              <AdminLocaleFlag locale={locale} className="h-5 w-5" />
              <bdi lang={locale}>{t(locale)}</bdi>
              {active ? <span className="sr-only">({t("current")})</span> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function useAdminLocaleSwitcher(user: UserDto, onLocaleChanged?: () => Promise<void> | void) {
  const currentRequestLocale = useLocale();
  const t = useTranslations("common.adminLanguage");
  const router = useRouter();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState<AdminLocale | null>(null);
  const savingRef = useRef(false);
  const activeLocale = resolveAdminLocale(user.preferredLocale ?? currentRequestLocale);

  async function switchLocale(locale: AdminLocale) {
    if (locale === activeLocale || savingRef.current) {
      return;
    }

    savingRef.current = true;
    setSaving(locale);
    try {
      const updatedUser = await updateCurrentUserLocale({ locale });
      setAdminLocaleCookie(resolveAdminLocale(updatedUser.preferredLocale));
      queryClient.setQueryData<CurrentUserResponse | null>(
        getGetCurrentUserQueryKey(),
        (current) => current ? { ...current, user: updatedUser } : current,
      );
      await onLocaleChanged?.();
      router.refresh();
    } finally {
      savingRef.current = false;
      setSaving(null);
    }
  }

  return { t, activeLocale, saving, switchLocale };
}

export function AdminLanguageOptions({
  activeLocale,
  disabled,
  onSelect,
  optionRole = "menuitemradio",
}: {
  activeLocale: AdminLocale;
  disabled: boolean;
  onSelect: (locale: AdminLocale) => void;
  optionRole?: "menuitemradio" | "radio";
}) {
  const t = useTranslations("common.adminLanguage");

  return (
    <LanguageDropdownOptions
      activeCode={activeLocale}
      currentLabel={t("current")}
      disabled={disabled}
      onSelect={(_, option) => onSelect(option.code)}
      optionRole={optionRole}
      options={locales.map((locale) => ({
        code: locale,
        name: t(locale),
      }))}
    />
  );
}

export function AdminLocaleFlag({ locale, className }: { locale: AdminLocale; className: string }) {
  return <LanguageFlag code={locale} className={className} />;
}
